import { Link } from '../../../components/Link';
import {
  Lead,
  LearnCode,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
  Tok,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('dmarc');

export default function DmarcPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'DMARC is the policy layer on top of SPF and DKIM. It requires that one of them passes AND belongs to the domain in the visible From: address, then tells receivers what to do when neither does: nothing, quarantine, or reject. It is the only one of the three that carries an instruction.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="The record, tag by tag">
        <LearnCode>
          {`_dmarc.yourdomain.com  TXT  "v=DMARC1; p=quarantine; pct=25; rua=mailto:dmarc@yourdomain.com; sp=reject; adkim=s"`}
        </LearnCode>
        <LearnTable
          head={['Tag', 'Does', 'Default if omitted']}
          rows={[
            ['v=DMARC1', 'Version. Must be first', <Lead key="a">Required</Lead>],
            ['p=', 'Policy: none, quarantine or reject', <Lead key="b">Required</Lead>],
            ['pct=', 'Percentage of failing mail the policy applies to', '100'],
            ['rua=', 'Where to send aggregate reports', 'None — and without it you are blind'],
            ['ruf=', 'Where to send per-message forensic reports', 'None. Rarely honoured now'],
            ['sp=', 'Policy for subdomains', 'Inherits p='],
            ['adkim=', 'DKIM alignment: r (relaxed) or s (strict)', 'r'],
            ['aspf=', 'SPF alignment: r or s', 'r'],
            ['fo=', 'When to generate forensic reports', '0'],
          ]}
        />
        <p>
          Two defaults worth knowing. <Tok>sp=</Tok> inherits, so publishing{' '}
          <Tok>p=reject</Tok> silently applies reject to every subdomain including ones you forgot
          about. And <Tok>ruf=</Tok> is largely dead — most large receivers stopped sending forensic
          reports on privacy grounds, so aggregate reports are the ones that matter.
        </p>
      </LearnSection>

      <LearnSection title="What each policy actually does">
        <LearnTable
          head={['Policy', 'Receiver behaviour', 'Protects you?', 'Use when']}
          rows={[
            ['p=none', 'Nothing. Deliver as normal, send reports', <Lead key="a">No</Lead>, 'Weeks 1–6, while you read reports'],
            ['p=quarantine', 'Route failing mail to spam', 'Partly', 'Once reports are clean, as a ramp step'],
            ['p=reject', 'Refuse at SMTP time — the message never lands', <Lead key="b">Yes</Lead>, 'The destination'],
          ]}
        />
        <p>
          <Tok>p=none</Tok> is where most domains stop, and it is worth being blunt about what that
          means: it is monitoring, not protection. A domain on <Tok>p=none</Tok> can be spoofed as
          freely as one with no DMARC record at all. The only thing it buys is visibility — which is
          genuinely valuable, and is not the same as safety.
        </p>
      </LearnSection>

      <LearnSection title="The ramp, with realistic timings">
        <p>
          The point of ramping is to find your own forgotten senders before enforcement finds them
          for you. Every organisation has some: the invoicing system, the CRM, the monitoring
          alerts, the thing a departed colleague set up in 2021.
        </p>
        <LearnTable
          head={['Stage', 'Record', 'Stay here until']}
          rows={[
            ['1. Observe', <Tok key="a">p=none; rua=…</Tok>, 'Reports account for 100% of legitimate volume — 2 to 6 weeks'],
            ['2. Fix', <Tok key="b">p=none; rua=…</Tok>, 'Every legitimate source authenticates and aligns'],
            ['3. Toe in', <Tok key="c">p=quarantine; pct=10</Tok>, 'One to two weeks, watching for complaints'],
            ['4. Ramp', <Tok key="d">p=quarantine; pct=50 → 100</Tok>, 'Two to four weeks total'],
            ['5. Enforce', <Tok key="e">p=reject</Tok>, 'Permanent. Keep reading reports'],
          ]}
        />
        <p>
          <Lead>How pct actually works</Lead> is misread constantly. It is not "apply the policy to
          10% of mail". It is: for each failing message, with probability{' '}
          <Tok>pct</Tok>, apply the policy — otherwise apply the <em>next weaker</em> one. So{' '}
          <Tok>p=quarantine; pct=10</Tok> quarantines roughly a tenth of failures and delivers the
          rest normally. Under <Tok>p=reject; pct=10</Tok>, the other 90% are quarantined, not
          delivered.
        </p>
      </LearnSection>

      <LearnSection title="Reading aggregate reports">
        <p>
          They arrive as gzipped XML, once a day per reporting receiver, and they are not designed
          to be read by humans. Each record is a group of messages sharing a source IP and result:
        </p>
        <LearnCode>
          {`<record>
  <row>
    <source_ip>203.0.113.7</source_ip>
    <count>142</count>
    <policy_evaluated><disposition>none</disposition>
      <dkim>pass</dkim><spf>fail</spf></policy_evaluated>
  </row>
  <identifiers><header_from>yourdomain.com</header_from></identifiers>
</record>`}
        </LearnCode>
        <p>
          What to look for, in order: sources with high <Tok>count</Tok> and both results failing
          (either spoofing or a forgotten sender of yours), then sources where{' '}
          <Tok>spf</Tok> passes but the disposition still fails — that is an{' '}
          <Link to="/learn/dmarc-alignment" className="text-accent underline underline-offset-4">
            alignment problem
          </Link>
          , not an authentication one, and it is the most common thing these reports surface.
        </p>
        <p>
          Send <Tok>rua</Tok> to an address you will actually process. A mailbox nobody opens is the
          usual reason a domain sits on <Tok>p=none</Tok> for two years.
        </p>
      </LearnSection>

      <LearnSection title="Mistakes that cost real mail">
        <>
          <LearnSubsection title="Publishing p=reject on day one">
            <p>
              The reports exist precisely because you do not know all your senders. Enforcing before
              reading them means discovering the invoicing system by way of unpaid invoices.
            </p>
          </LearnSubsection>
          <LearnSubsection title="Forgetting sp= on parked domains">
            <p>
              <Tok>sp=</Tok> inherits <Tok>p=</Tok>, which is usually what you want — but a domain
              you do not send from at all should carry <Tok>p=reject; sp=reject</Tok> from the
              start. There is no ramp needed for mail that never legitimately exists.
            </p>
          </LearnSubsection>
          <LearnSubsection title="Two DMARC records">
            <p>
              Same rule as SPF: exactly one. Two records at <Tok>_dmarc</Tok> means receivers apply
              no policy at all, which looks identical to having none.
            </p>
          </LearnSubsection>
          <LearnSubsection title="Treating a clean report as done">
            <p>
              Reports show what receivers saw during that window. A quiet week does not mean a new
              sender was not added on Monday. This is monitoring, not a certificate.
            </p>
          </LearnSubsection>
        </>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'What does p=none actually protect against?',
    a: 'Nothing. It is monitor-only: mail is delivered exactly as it would be without any DMARC record, and reports are sent. A domain on p=none can be spoofed as freely as one with no record. What it buys is visibility, which is the necessary first step and not protection.',
  },
  {
    q: 'What is the difference between quarantine and reject?',
    a: 'Quarantine asks receivers to treat failing mail as suspicious, which in practice means the spam folder. Reject refuses the message at SMTP time so it is never delivered at all and the sender gets a bounce. Reject is the end state; quarantine is the ramp toward it.',
  },
  {
    q: 'How does the pct tag work?',
    a: 'For each failing message, with probability pct the policy is applied; otherwise the next weaker policy is. So p=quarantine with pct=10 quarantines about a tenth of failures and delivers the rest, and p=reject with pct=10 rejects a tenth and quarantines the other ninety percent.',
  },
  {
    q: 'How long should I stay on p=none?',
    a: 'Until aggregate reports account for essentially all of your legitimate volume and every source authenticates and aligns. Two to six weeks is typical. Sitting there for a year usually means the rua address is a mailbox nobody opens.',
  },
  {
    q: 'Do I need DMARC if I already have SPF and DKIM?',
    a: 'Yes, for two reasons. SPF and DKIM check domains that a reader never sees, so without DMARC nothing ties authentication to the visible From: address. And neither carries an instruction — DMARC is the only record that tells a receiver to refuse.',
  },
  {
    q: 'What about domains I never send mail from?',
    a: 'Publish v=DMARC1; p=reject; sp=reject straight away, alongside an SPF record of v=spf1 -all. There is no legitimate mail to break, so there is no ramp to run, and parked domains are attractive spoofing targets precisely because they are unwatched.',
  },
];
